<template>
  <div class="pending-fees-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Pending Fees</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ displayTotalStudents }} Students
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem;" />
      <p class="mt-3 font-weight-bold">Loading pending fee records...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3">Retry</b-button>
    </div>

    <div v-else class="report-page">
      <!-- Compact header: stats inline, no separate cards on print -->
      <div class="report-header-compact">
        <img v-if="schoolLogo" :src="schoolLogo" alt="Logo" class="logo-sm" />
        <div class="header-body">
          <div class="school-line">
            {{ campusName }}<span v-if="campusBranch"> — {{ campusBranch }}</span>
          </div>
          <div class="meta-line">
            <strong>Pending Fees</strong>
            · {{ currentDateTime }}
            · Class: {{ classFilterLabel }}
            · Section: {{ sectionFilterLabel }}
          </div>
          <div class="stats-line">
            <span><strong>Students:</strong> {{ displayTotalStudents }}</span>
            <span class="stat-sep">|</span>
            <span><strong>Total Unpaid:</strong> {{ formatCurrency(displayTotalUnpaidAmount) }}</span>
          </div>
        </div>
      </div>

      <div v-if="flatRows.length === 0" class="text-center py-4 no-data">
        <p>No pending fee records found for the selected criteria</p>
      </div>

      <table v-else class="compact-table">
        <thead>
          <tr>
            <th class="c-sno">#</th>
            <th class="c-gr">GR</th>
            <th class="c-name">Student</th>
            <th class="c-father">Father</th>
            <th class="c-cls">Class / Sec</th>
            <th class="c-adm">ADM Date</th>
            <th class="c-fees">Pending Fees</th>
            <th class="c-total text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(row, rowIndex) in flatRows">
            <tr
              v-if="row.type === 'section'"
              :key="`sec-${rowIndex}`"
              class="section-row"
            >
              <td colspan="8">
                {{ row.group.className }} — Section {{ row.group.sectionName || 'N/A' }}
                ({{ row.group.studentCount }} student{{ row.group.studentCount === 1 ? '' : 's' }},
                {{ formatCurrency(row.group.groupUnpaidAmount) }})
              </td>
            </tr>
            <tr v-else :key="`stu-${row.student.id}-${rowIndex}`" class="student-row">
              <td class="text-center">{{ row.sno }}</td>
              <td class="text-center">{{ row.student.grNo || '-' }}</td>
              <td class="name-cell">{{ (row.student.name || '-').toUpperCase() }}</td>
              <td>{{ (row.student.father_name || '-').toUpperCase() }}</td>
              <td class="text-center cls-cell">
                {{ row.group.className }}<span v-if="row.group.sectionName"> / {{ row.group.sectionName }}</span>
              </td>
              <td class="text-center adm-cell">{{ formatDateShort(row.student.date_of_admission) }}</td>
              <td class="fees-cell">
                <span
                  v-for="(fee, feeIdx) in row.student.feeMonths"
                  :key="`${row.student.id}-fee-${feeIdx}`"
                  class="fee-item"
                >
                  <span class="fee-month">{{ formatMonthShort(fee) }}</span>
                  <span class="fee-type">{{ shortFeeType(fee.feeType) }}</span>
                  <span class="fee-amt">{{ formatCurrency(fee.netFee) }}</span>
                </span>
              </td>
              <td class="text-right total-cell">{{ formatCurrency(row.student.displayTotal) }}</td>
            </tr>
          </template>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="7" class="text-right">Grand Total</td>
            <td class="text-right">{{ formatCurrency(displayTotalUnpaidAmount) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from 'bootstrap-vue'
import axios from 'axios'

export default {
  name: 'PendingFeesAfterAdmissionPrint',
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      processedGroups: [],
      campusName: 'MY SKOOL',
      campusBranch: '',
      schoolLogo: null,
      db: '',
      cID: '',
      classID: 0,
      sectionID: 0,
      classFilterLabel: 'All Classes',
      sectionFilterLabel: 'All Sections',
    }
  },
  computed: {
    isMyskoolstjacobDomain() {
      const reportDomain = (this.$store.state.report || '').replace(/_/g, '')
      return (
        reportDomain === 'myskoolstjacob' ||
        window.location.hostname === 'myskoolstjacob.myskool.app'
      )
    },
    currentDateTime() {
      const now = new Date()
      return now.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    displayTotalStudents() {
      return this.processedGroups.reduce((sum, g) => sum + (g.students?.length || 0), 0)
    },
    displayTotalUnpaidAmount() {
      return this.processedGroups.reduce(
        (sum, g) => sum + (Number(g.groupUnpaidAmount) || 0),
        0
      )
    },
    flatRows() {
      const rows = []
      let sno = 0
      this.processedGroups.forEach((group) => {
        rows.push({ type: 'section', group })
        group.students.forEach((student) => {
          sno += 1
          rows.push({ type: 'student', group, student, sno })
        })
      })
      return rows
    },
  },
  created() {
    if (!this.isMyskoolstjacobDomain) {
      this.error = 'This report is only available for Myskool St. Jacob.'
      return
    }
    this.initReport()
  },
  methods: {
    initReport() {
      const query = this.$route.query
      this.db = query.db || this.$store.state.userData.db
      this.cID = query.cID || query.cId || 0
      this.classID = Number(query.classID) || 0
      this.sectionID = Number(query.sectionID) || 0
      this.classFilterLabel = query.className || (this.classID ? `Class ID ${this.classID}` : 'All Classes')
      this.sectionFilterLabel =
        query.sectionName || (this.sectionID ? `Section ID ${this.sectionID}` : 'All Sections')

      if (!this.db) {
        this.error = 'Database identifier is missing. Please close and try again.'
        return
      }

      this.loadCampusInfo()
      this.loadReportData()
    },
    isExemptedFee(fee) {
      const status = String(fee?.status || '').toLowerCase().trim()
      return status === 'exempted' || status === 'exempt' || status === 'exemption'
    },
    processReportGroups(rawGroups) {
      if (!Array.isArray(rawGroups)) return []

      return rawGroups
        .map((group) => {
          const students = (group.students || [])
            .map((student) => {
              const feeMonths = (student.feeMonths || []).filter(
                (fee) => !this.isExemptedFee(fee)
              )
              const displayTotal = feeMonths.reduce(
                (sum, fee) => sum + (Number(fee.netFee) || 0),
                0
              )
              return { ...student, feeMonths, displayTotal }
            })
            .filter((student) => student.feeMonths.length > 0)

          const groupUnpaidAmount = students.reduce(
            (sum, student) => sum + student.displayTotal,
            0
          )

          return {
            ...group,
            students,
            studentCount: students.length,
            groupUnpaidAmount,
          }
        })
        .filter((group) => group.students.length > 0)
    },
    formatFeeSummary(feeMonths) {
      if (!feeMonths || !feeMonths.length) return '-'
      return feeMonths
        .map((fee) => {
          const month = this.formatMonthShort(fee)
          const type = this.shortFeeType(fee.feeType)
          const amt = this.formatCurrency(fee.netFee)
          return `${month} ${type} ${amt}`
        })
        .join('  |  ')
    },
    formatMonthShort(fee) {
      const raw = fee.monthYear || (fee.monthVal && fee.year ? `${fee.monthVal}-${fee.year}` : '')
      if (!raw) return '-'
      return String(raw).replace(/\s+/g, ' ').trim()
    },
    shortFeeType(type) {
      const t = String(type || '').trim()
      if (!t) return '-'
      if (t.toLowerCase().includes('tuition')) return 'Tuition'
      if (t.toLowerCase().includes('annual')) return 'Annual'
      if (t.toLowerCase().includes('late')) return 'Late'
      return t.length > 10 ? t.slice(0, 10) : t
    },
    async loadCampusInfo() {
      try {
        const url = `${this.$store.state.domain}campuses`
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: 'application/json',
          },
        })

        if (response.data?.status === 'success' && Array.isArray(response.data.data)) {
          const match = response.data.data.find((c) => c.id == this.cID)
          if (match) {
            this.campusName = match.name
            this.campusBranch = this.cID == 0 ? 'All Campuses' : match.branch
            this.schoolLogo = match.logo
          }
        }
      } catch (e) {
        const currentBranch = this.$store.state.currentBranch
        if (currentBranch) {
          this.campusName = currentBranch.organizationName || currentBranch.branch || 'MY SKOOL'
          this.campusBranch = currentBranch.branch || ''
        }
      }
    },
    async loadReportData() {
      this.loading = true
      this.error = null

      try {
        const url = `${this.$store.state.domain}Fees/NoFeeSubmittedAfterAdmissionReport`
        const response = await axios.get(url, {
          params: {
            db: this.db,
            cID: this.cID,
            classID: this.classID || 0,
            sectionID: this.sectionID || 0,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: 'application/json',
          },
        })

        if (response.data?.status === 'success') {
          this.processedGroups = this.processReportGroups(response.data.data || [])
        } else {
          this.error = response.data?.message || 'Failed to load report data.'
        }
      } catch (err) {
        console.error('Pending fees report error:', err)
        this.error = 'Failed to retrieve report data. Please try again.'
      } finally {
        this.loading = false
      }
    },
    formatCurrency(amount) {
      const num = Number(amount) || 0
      return num.toLocaleString('en-PK', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    },
    formatDateShort(dateStr) {
      if (!dateStr) return '-'
      try {
        const d = new Date(dateStr)
        return d.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: '2-digit',
        })
      } catch {
        return dateStr
      }
    },
    print() {
      window.print()
    },
    close() {
      window.close()
    },
  },
}
</script>

<style lang="scss" scoped>
.pending-fees-report {
  background: #eef1f5;
  min-height: 100vh;
  padding: 1rem;
  font-family: Arial, 'Segoe UI', sans-serif;
  color: #111;
  font-size: 13px;
}

.control-bar {
  background: #fff;
  padding: 0.75rem 1.25rem;
  margin-bottom: 1rem;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
}

.btn-modern {
  padding: 7px 16px;
  border-radius: 5px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  margin-left: 8px;
}

.btn-print {
  background: #4a5568;
  color: #fff;
}

.btn-close {
  background: #fff;
  border: 1px solid #ccc;
}

.report-page {
  background: #fff;
  padding: 10px 12px;
  max-width: 100%;
}

/* ---- Compact header ---- */
.report-header-compact {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border-bottom: 2px solid #000;
  padding-bottom: 6px;
  margin-bottom: 8px;

  .logo-sm {
    height: 48px;
    width: auto;
    object-fit: contain;
    flex-shrink: 0;
  }

  .school-line {
    font-size: 15px;
    font-weight: 800;
    text-transform: uppercase;
    line-height: 1.2;
  }

  .meta-line,
  .stats-line {
    font-size: 12px;
    color: #333;
    line-height: 1.4;
    margin-top: 3px;
  }

  .stats-line {
    font-weight: 600;

    .stat-sep {
      margin: 0 6px;
      color: #999;
    }
  }
}

/* ---- Single dense table ---- */
.compact-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;

  th,
  td {
    border: 1px solid #555;
    padding: 6px 7px;
    vertical-align: top;
    line-height: 1.4;
  }

  thead th {
    background: #e8e8e8;
    font-weight: 800;
    font-size: 12px;
    text-transform: uppercase;
    padding: 7px 6px;
    vertical-align: middle;
  }

  .c-sno { width: 4%; }
  .c-gr { width: 6%; }
  .c-name { width: 11%; }
  .c-father { width: 11%; }
  .c-cls { width: 8%; }
  .c-adm { width: 8%; }
  .c-fees { width: 46%; }
  .c-total { width: 6%; }

  .section-row td {
    background: #d8d8d8;
    font-weight: 800;
    font-size: 12px;
    padding: 6px 7px;
    text-transform: uppercase;
  }

  .student-row:nth-child(even) {
    background: #fafafa;
  }

  .name-cell {
    font-weight: 700;
    font-size: 13px;
  }

  .cls-cell,
  .adm-cell {
    font-size: 12px;
    color: #333;
  }

  .fees-cell {
    font-size: 12px;
    line-height: 1.5;
    word-break: break-word;
  }

  .fee-item {
    display: inline-block;
    margin: 2px 8px 4px 0;
    padding: 2px 0;
    white-space: nowrap;
  }

  @media screen and (max-width: 900px) {
    .fee-item {
      white-space: normal;
    }
  }

  .fee-item .fee-month {
    font-size: 13px;
    font-weight: 800;
    color: #1a1a1a;
    margin-right: 4px;
  }

  .fee-item .fee-type {
    font-size: 12px;
    font-weight: 600;
    color: #444;
    margin-right: 4px;
  }

  .fee-item .fee-amt {
    font-size: 13px;
    font-weight: 700;
    color: #b91c1c;
  }

  .total-cell {
    font-weight: 800;
    font-size: 13px;
    white-space: nowrap;
    vertical-align: middle;
  }

  tfoot td {
    background: #ccc;
    font-weight: 800;
    font-size: 13px;
    padding: 8px 7px;
    border-top: 2px solid #000;
  }
}

.no-data p {
  margin: 0;
  color: #666;
}

@page {
  size: A4 portrait;
  margin: 8mm 6mm;
}

@media print {
  @page {
    size: A4 portrait;
    margin: 8mm 6mm;
  }

  .no-print {
    display: none !important;
  }

  .pending-fees-report {
    background: #fff !important;
    padding: 0 !important;
  }

  .report-page {
    padding: 0 !important;
  }

  .report-header-compact {
    margin-bottom: 4px !important;
    padding-bottom: 3px !important;
    border-bottom-width: 1px !important;

    .logo-sm {
      height: 36px !important;
    }

    .school-line {
      font-size: 12pt !important;
    }

    .meta-line,
    .stats-line {
      font-size: 8.5pt !important;
    }
  }

  .compact-table {
    font-size: 9.5pt !important;

    th,
    td {
      padding: 4px 5px !important;
      border-color: #000 !important;
      line-height: 1.35 !important;
    }

    thead th {
      font-size: 9pt !important;
      padding: 5px 4px !important;
      background: #eee !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .section-row td {
      background: #ddd !important;
      font-size: 9.5pt !important;
      padding: 5px 6px !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .fees-cell {
      font-size: 9pt !important;
      line-height: 1.45 !important;
    }

    .fee-item {
      white-space: normal !important;
      margin: 2px 6px 3px 0 !important;
    }

    .fee-item .fee-month {
      font-size: 9.5pt !important;
      font-weight: 800 !important;
      color: #000 !important;
    }

    .fee-item .fee-type {
      font-size: 9pt !important;
    }

    .fee-item .fee-amt {
      font-size: 9.5pt !important;
      color: #000 !important;
    }

    .name-cell {
      font-size: 9.5pt !important;
    }

    .cls-cell,
    .adm-cell {
      font-size: 9pt !important;
    }

    .total-cell {
      font-size: 9.5pt !important;
    }

    tfoot td {
      font-size: 10pt !important;
      background: #ddd !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  /* Allow rows to flow — no forced page breaks per student */
  tr {
    page-break-inside: auto !important;
  }

  thead {
    display: table-header-group;
  }

  tfoot {
    display: table-footer-group;
  }
}
</style>
